// Catalog.ID Client — Credential Generation
// ============================================================================
// Generates passwords, supersecrets, and auth hashes for registration and login.

import { argon2id } from 'hash-wasm';
import * as aes from './aes';

/**
 * Generate a 12-character Base64 password.
 * Uses 9 random bytes → 12 Base64 characters.
 */
export function generatePassword(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(9));
    return aes.toBase64(bytes);
}

/** Generate from an arbitrary byte source — the entropy-ceremony path
 *  (crypto/entropy.ts). Same 9-bytes → 12-Base64-chars shape as
 *  {@link generatePassword}. */
export function generatePasswordFrom(nextByte: () => number): string {
    const bytes = new Uint8Array(9);
    for (let i = 0; i < bytes.length; i++) bytes[i] = nextByte();
    return aes.toBase64(bytes);
}

export { generateSupersecret } from './supersecret';

/**
 * Argon2id parameters for the client-side password pre-hash.
 *
 * The salt is SHA-256(cid) so:
 *   - it is per-account (kills wire-level correlation between users with the same password)
 *   - the client can derive it without a server round-trip at login (the user types the CID)
 *
 * The work factor (64 MB / 3 iterations / 4 lanes) matches the supersecret KDF in argon2.ts
 * and the whitepaper §3.4.3.
 */
const AUTH_HASH_ARGON2_PARAMS = {
    memorySize: 65536,
    iterations: 3,
    parallelism: 4,
    hashLength: 32,
} as const;

/**
 * Compute the auth hash sent to /api/account/register and /api/account/login.
 *
 * authHash = Argon2id(password, SHA-256(cid))
 *
 * The server applies PBKDF2-SHA512 with a random salt on top before storing, so the
 * stored hash is PBKDF2(Argon2id(password, SHA-256(cid)), random_salt). The CID-based
 * salt prevents on-wire correlation; the Argon2id work factor slows offline brute-force
 * if the auth hash is ever leaked from logs or in transit; the server-side PBKDF2 layer
 * defends against database compromise.
 *
 * For registration, call /api/account/register/begin first to obtain the CID.
 */
export async function computeAuthHash(password: string, cid: string): Promise<string> {
    const cidSalt = await aes.sha256(aes.encodeUtf8(cid));
    const hash = await argon2id({
        password,
        salt: cidSalt,
        ...AUTH_HASH_ARGON2_PARAMS,
        outputType: 'binary',
    });
    return aes.toBase64(new Uint8Array(hash));
}

/** Generate a random device token (32 bytes, hex-encoded). */
export function generateDeviceToken(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}
